import { useContext, useState, useEffect, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { BrandAdminContext } from "components/BrandAdmin/BrandAdmin-container";
import useGenericModal from "components/GenericModal/GenericModal-logic";
import useEditAccount from "hooks/useEditAccount";

const emptyInputs = {
  accountName: "",
  accountBrand: "",
};

const useUserAdminAccounts = () => {
  const { brandId } = useContext(BrandAdminContext);
  const [accounts, setAccounts] = useState([]);
  const { modalOpen, setModalOpen } = useGenericModal();
  const [inputs, setInputs] = useState(emptyInputs);
  const { handleSaveAccount, handleDeactivateAccount } = useEditAccount(brandId);
  const [shouldUpdateBrandAccounts, updateBrandAccounts] = useState(false);

  // Auth readiness gate
  const [ready, setReady] = useState(false);

  // Prevent duplicate fetches (StrictMode / rapid re-renders)
  const inFlight = useRef(false);

  // Track auth state; only set ready=true when a session exists
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setAccounts([]);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Fetch brand accounts once authed and when brandId or refresh flag changes
  useEffect(() => {
    if (!ready || !brandId) return;
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check session
        const brandData = await API.get(
          "AccountGatewayApi",
          `/brand/${brandId}`);
        if (!cancelled) {
          setAccounts(brandData.accounts || []);
          updateBrandAccounts(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("fetch Brand accounts error", err);
        }
      } finally {
        inFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, brandId, shouldUpdateBrandAccounts]);

  return {
    accounts,
    modalOpen,
    setModalOpen,
    inputs,
    setInputs,
    handleSaveAccount,
    brandId,
    updateBrandAccounts,
    handleDeactivateAccount,
  };
};

export default useUserAdminAccounts;
